import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseFormModal, FieldHint, type FormModalMode } from 'components/enterprise-shell';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import BusinessUnitSelect from 'components/common/org/BusinessUnitSelect';
import { dineOpsService } from 'api/services/dineOpsService';
import { printService } from 'api/services/printService';
import type { DineKitchen, DineKitchenStation, KitchenStationLine, PrinterLookup } from 'api/types';

/**
 * ============================================================================
 * KITCHEN FORM MODAL (SETUP-009)
 * ============================================================================
 * A kitchen and its kitchen stations, saved together. The kitchen links to an
 * organisation unit (usually the F&B department or a cost centre under it); its
 * stations take their unit from it. A station row is where a ticket prints:
 * code, name, printer, fallback. Rarely used printing (backup printer, copies,
 * room service and takeaway printers, the browser label) opens under the row.
 *
 * + Add station creates a new one; Add existing moves a station here from no
 * kitchen or another kitchen; Move out takes a station out of this kitchen and
 * keeps it; Delete (administrators) deletes it on save, refused by the server
 * while anything still routes or falls back to it. A new station is used by no
 * outlet until the outlet ticks it (Outlets → Kitchen Stations). Opening and
 * closing a station is on the Kitchen Stations screen.
 *
 * A kitchen with one counter (a Bar) needs no second entry: while the table is
 * empty, "Also use this kitchen as its station" (ticked for a new kitchen) saves
 * one station with the kitchen's code and name, and its printer and fallback.
 * From then on the two are separate records.
 * ============================================================================
 */

export interface PropertyOption { id: string; name: string; entityId?: string }

/** A station row; `key` keeps React rows stable for stations not saved yet. */
interface StationRow extends KitchenStationLine { key: string }

export interface KitchenForm {
  kitchenCode: string;
  kitchenName: string;
  /** '' = shared by every property. */
  propertyId: string;
  businessUnitId: string;
  displayOrder: string;
  isActive: boolean;
  stations: StationRow[];
  /** Saved stations marked for deletion in this save (kept for Undo). */
  deletedStations: StationRow[];
  /** While `stations` is empty: save one station with the kitchen's code and name. */
  asStation: boolean;
  asStationPrinterId: string;
  asStationFallbackId: string;
}

const EMPTY_FORM: KitchenForm = {
  kitchenCode: '', kitchenName: '', propertyId: '', businessUnitId: '', displayOrder: '10', isActive: true, stations: [],
  deletedStations: [], asStation: true, asStationPrinterId: '', asStationFallbackId: '',
};

/** Unit types a kitchen links to. */
const KITCHEN_UNIT_TYPES = ['DEPARTMENT', 'COST_CENTER', 'DIVISION'];
const CODE_PATTERN = /^[A-Z0-9_]{2,30}$/;

let rowSeq = 0;
const newKey = () => `new-${Date.now()}-${rowSeq++}`;
const blankStation = (): StationRow => ({
  key: newKey(), stationCode: '', stationName: '', primaryPrinterId: null, backupPrinterId: null, printCopies: 1,
  printerName: null, roomServicePrinterId: null, takeawayPrinterId: null, fallbackStationId: null, isActive: true, rowVersion: 0,
});
const fromStation = (s: DineKitchenStation): StationRow => ({
  key: s.id, id: s.id, stationCode: s.stationCode, stationName: s.stationName,
  primaryPrinterId: s.primaryPrinterId ?? null, backupPrinterId: s.backupPrinterId ?? null, printCopies: s.printCopies ?? 1,
  printerName: s.printerName ?? null,
  roomServicePrinterId: s.channelPrinters?.find((c) => c.orderChannel === 'ROOM_SERVICE')?.printerId ?? null,
  takeawayPrinterId: s.channelPrinters?.find((c) => c.orderChannel === 'TAKEAWAY')?.printerId ?? null,
  fallbackStationId: s.fallbackStationId ?? null, isActive: s.isActive, isOpen: s.isOpen, usedBy: s.usedBy, rowVersion: s.rowVersion,
});

interface Props {
  isOpen: boolean;
  mode: FormModalMode;
  /** For edit and copy, the kitchen with its stations (GET by id). */
  record: DineKitchen | null;
  properties: PropertyOption[];
  /** A new kitchen starts in this property (the user's default). */
  defaultPropertyId?: string;
  /** Delete on a station row (administrators); everyone may move a station out. */
  canDeleteStations: boolean;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;
}

// ------------------------------------------------------------------ the stations table

interface StationsEditorProps {
  rows: StationRow[];
  onChange: (rows: StationRow[]) => void;
  /** Saved stations marked for deletion, and the setter (Delete moves a row here, Undo moves it back). */
  deleted: StationRow[];
  onDeletedChange: (rows: StationRow[]) => void;
  canDelete: boolean;
  /** The kitchen's own code and name, and the single-station option shown while the table is empty. */
  kitchenLabel: string;
  asStation: boolean;
  asStationPrinterId: string;
  asStationFallbackId: string;
  onAsStationChange: (patch: { asStation?: boolean; asStationPrinterId?: string; asStationFallbackId?: string }) => void;
  kitchenId?: string;
  disabled: boolean;
  error?: string;
}

const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' };
const td: React.CSSProperties = { padding: '0.35rem 0.5rem', verticalAlign: 'top', borderBottom: '1px solid var(--border-color)' };

const StationsEditor: React.FC<StationsEditorProps> = ({
  rows, onChange, deleted, onDeletedChange, canDelete, kitchenLabel, asStation, asStationPrinterId, asStationFallbackId,
  onAsStationChange, kitchenId, disabled, error,
}) => {
  const [printers, setPrinters] = useState<PrinterLookup[]>([]);
  const [allStations, setAllStations] = useState<DineKitchenStation[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    printService.getPrinterLookup().then(setPrinters).catch(() => setPrinters([]));
    dineOpsService.getStations({ pageSize: 100, includeInactive: true }).then((r) => setAllStations(r.data)).catch(() => setAllStations([]));
  }, []);

  const printerOptions: SelectOption[] = useMemo(
    () => printers.map((p) => ({ value: p.id, label: `${p.printerName}${p.agentName ? ` · ${p.agentName}` : ''}` })), [printers]);

  // A fallback is another existing, active station; this kitchen's come first.
  const fallbackOptions = useCallback((row: StationRow): SelectOption[] => allStations
    .filter((s) => s.id !== row.id && s.isActive)
    .sort((a, b) => Number(b.kitchenId === kitchenId) - Number(a.kitchenId === kitchenId) || a.stationName.localeCompare(b.stationName))
    .map((s) => ({ value: s.id, label: s.kitchenName ? `${s.stationName} · ${s.kitchenName}` : s.stationName })), [allStations, kitchenId]);

  // Stations that can be moved here: any not already in the table or marked for deletion.
  const existingOptions: SelectOption[] = useMemo(() => allStations
    .filter((s) => !rows.some((r) => r.id === s.id) && !deleted.some((d) => d.id === s.id))
    .map((s) => ({ value: s.id, label: `${s.stationName} (${s.stationCode})${s.kitchenName ? ` — in ${s.kitchenName}` : ' — no kitchen'}` })),
  [allStations, rows, deleted]);

  const update = (key: string, patch: Partial<StationRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) => onChange(rows.filter((r) => r.key !== key));
  const markDeleted = (row: StationRow) => { onChange(rows.filter((r) => r.key !== row.key)); onDeletedChange([...deleted, row]); };
  const undoDelete = (row: StationRow) => { onDeletedChange(deleted.filter((d) => d.key !== row.key)); onChange([...rows, row]); };
  // The single-station fallback: any existing active station.
  const asStationFallbackOptions: SelectOption[] = allStations.filter((s) => s.isActive)
    .map((s) => ({ value: s.id, label: s.kitchenName ? `${s.stationName} · ${s.kitchenName}` : s.stationName }));
  const toggle = (key: string) => setExpanded((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const addNew = () => { const row = blankStation(); onChange([...rows, row]); };
  const addExisting = (id: string) => {
    const s = allStations.find((x) => x.id === id);
    if (s) onChange([...rows, fromStation(s)]);
  };

  return (
    <div data-field="stations">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="form-label" style={{ margin: 0 }}>Kitchen stations</div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ minWidth: 260 }}>
            <SyutSelect options={existingOptions} value="" onChange={(v) => { if (v) addExisting(v as string); }}
              placeholder="Add existing station…" isDisabled={disabled || existingOptions.length === 0} size="sm" menuPortalTarget={document.body} />
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addNew} disabled={disabled}>
            <IconSystem.add size={14} /> Add station
          </button>
        </div>
      </div>
      {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.375rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 130 }}>Code *</th>
              <th style={th}>Station name *</th>
              <th style={{ ...th, width: 210 }}>Printer</th>
              <th style={{ ...th, width: 200 }}>Fallback station</th>
              <th style={{ ...th, width: 60 }}>Active</th>
              <th style={{ ...th, width: 70 }} aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={td} colSpan={6} data-as-station>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: disabled ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={asStation} disabled={disabled}
                      onChange={(e) => onAsStationChange({ asStation: e.target.checked })}
                      style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }} />
                    <span>Also use this kitchen as its station{kitchenLabel ? <> (<strong>{kitchenLabel}</strong>)</> : ''}</span>
                  </label>
                  {asStation ? (
                    <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
                      <SyutSelect label="Printer" options={printerOptions} value={asStationPrinterId} isClearable size="sm" isDisabled={disabled}
                        placeholder="Screen only" menuPortalTarget={document.body}
                        onChange={(v) => onAsStationChange({ asStationPrinterId: (v as string) || '' })} />
                      <SyutSelect label="Fallback station" options={asStationFallbackOptions} value={asStationFallbackId} isClearable size="sm"
                        isDisabled={disabled} placeholder="No fallback" menuPortalTarget={document.body}
                        onChange={(v) => onAsStationChange({ asStationFallbackId: (v as string) || '' })} />
                    </div>
                  ) : (
                    <FieldHint>No stations yet. Add the places where this kitchen's tickets print, e.g. Tandoor, Chinese, Pastry; a kitchen with no station receives no tickets.</FieldHint>
                  )}
                  <FieldHint>For a kitchen with one counter, such as a Bar: one station is saved with the kitchen's code and name. Add more rows instead when it has several stations.</FieldHint>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.key}>
                <tr data-station-line>
                  <td style={td}>
                    <input className="form-input" value={r.stationCode} maxLength={30} disabled={disabled} placeholder="TANDOOR"
                      onChange={(e) => update(r.key, { stationCode: e.target.value.toUpperCase() })} aria-label="Station code" />
                  </td>
                  <td style={td}>
                    <input className="form-input" value={r.stationName} maxLength={100} disabled={disabled} placeholder="Tandoor"
                      onChange={(e) => update(r.key, { stationName: e.target.value })} aria-label="Station name" />
                    {r.usedBy && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>Used by {r.usedBy}</div>}
                  </td>
                  <td style={td}>
                    <SyutSelect options={printerOptions} value={r.primaryPrinterId ?? ''} isClearable size="sm" isDisabled={disabled}
                      placeholder="Screen only" menuPortalTarget={document.body}
                      onChange={(v) => update(r.key, { primaryPrinterId: (v as string) || null })} />
                  </td>
                  <td style={td}>
                    <SyutSelect options={fallbackOptions(r)} value={r.fallbackStationId ?? ''} isClearable size="sm" isDisabled={disabled}
                      placeholder="No fallback" menuPortalTarget={document.body}
                      onChange={(v) => update(r.key, { fallbackStationId: (v as string) || null })} />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input type="checkbox" checked={r.isActive} disabled={disabled} onChange={(e) => update(r.key, { isActive: e.target.checked })} aria-label="Active" />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button type="button" className="modal-header-btn" title="More printing settings" onClick={() => toggle(r.key)} aria-expanded={expanded.has(r.key)}>
                      <IconSystem.moreHorizontal size={16} />
                    </button>
                    <button type="button" className="modal-header-btn" disabled={disabled} onClick={() => remove(r.key)}
                      title={r.id ? 'Move out of this kitchen (the station is kept, with no kitchen)' : 'Remove this new station'}>
                      <IconSystem.x size={16} />
                    </button>
                    {r.id && canDelete && (
                      <button type="button" className="modal-header-btn" disabled={disabled} onClick={() => markDeleted(r)}
                        title="Delete the station when you save (refused while dishes route or stations fall back to it)">
                        <IconSystem.trash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
                {expanded.has(r.key) && (
                  <tr>
                    <td style={{ ...td, background: 'var(--bg-secondary)' }} colSpan={6}>
                      {!r.primaryPrinterId && <FieldHint>Choose a printer first: without one, tickets show on the kitchen screen and print from the browser.</FieldHint>}
                      <div className="form-grid-4">
                        <SyutSelect label="Backup printer" options={printerOptions.filter((o) => o.value !== r.primaryPrinterId)} value={r.backupPrinterId ?? ''}
                          isClearable size="sm" isDisabled={disabled || !r.primaryPrinterId} placeholder="No backup" menuPortalTarget={document.body}
                          onChange={(v) => update(r.key, { backupPrinterId: (v as string) || null })} />
                        <SyutSelect label="Room service printer" options={printerOptions} value={r.roomServicePrinterId ?? ''}
                          isClearable size="sm" isDisabled={disabled || !r.primaryPrinterId} placeholder="Same as the printer" menuPortalTarget={document.body}
                          onChange={(v) => update(r.key, { roomServicePrinterId: (v as string) || null })} />
                        <SyutSelect label="Takeaway printer" options={printerOptions} value={r.takeawayPrinterId ?? ''}
                          isClearable size="sm" isDisabled={disabled || !r.primaryPrinterId} placeholder="Same as the printer" menuPortalTarget={document.body}
                          onChange={(v) => update(r.key, { takeawayPrinterId: (v as string) || null })} />
                        <div className="form-group">
                          <label className="form-label">Copies</label>
                          <input className="form-input" type="number" min={1} max={5} value={r.printCopies} disabled={disabled}
                            onChange={(e) => update(r.key, { printCopies: Number(e.target.value) || 1 })} />
                        </div>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Label on browser-printed tickets</label>
                        <input className="form-input" value={r.printerName ?? ''} maxLength={100} disabled={disabled} placeholder="Optional"
                          onChange={(e) => update(r.key, { printerName: e.target.value })} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {deleted.length > 0 && (
        <div role="status" style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--danger, #dc2626)' }}>
          Deleted when you save:{' '}
          {deleted.map((d, i) => (
            <span key={d.key}>
              {i > 0 && ', '}<strong>{d.stationName}</strong>{' '}
              <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={disabled} onClick={() => undoDelete(d)}>Undo</button>
            </span>
          ))}
        </div>
      )}
      <FieldHint>A new station is used by no outlet until the outlet ticks it on Outlets → Kitchen Stations. × moves a station out of this kitchen and keeps it.</FieldHint>
    </div>
  );
};

// ------------------------------------------------------------------ the modal

/** The station a one-counter kitchen saves when "Also use this kitchen as its station" is ticked. */
const kitchenAsStation = (f: KitchenForm): KitchenStationLine => ({
  stationCode: f.kitchenCode.trim().toUpperCase(), stationName: f.kitchenName.trim(),
  primaryPrinterId: f.asStationPrinterId || null, backupPrinterId: null, printCopies: 1, printerName: null,
  roomServicePrinterId: null, takeawayPrinterId: null, fallbackStationId: f.asStationFallbackId || null, isActive: true, rowVersion: 0,
});

const DineOpsKitchenFormModal: React.FC<Props> = ({
  isOpen, mode, record, properties, defaultPropertyId, canDeleteStations, tabId, moduleName, createInstanceId, onClose, onSaved, onRestoreModal,
}) => {
  const populateOnOpen = useCallback((m: FormModalMode, r: DineKitchen | null): KitchenForm => {
    if (!r || m === 'create') return { ...EMPTY_FORM, propertyId: defaultPropertyId ?? '', stations: [], deletedStations: [] };
    return {
      ...EMPTY_FORM,
      kitchenCode: m === 'copy' ? '' : r.kitchenCode,
      kitchenName: m === 'copy' ? '' : r.kitchenName,
      propertyId: r.propertyId ?? '',
      businessUnitId: r.businessUnitId ?? '',
      displayOrder: String(r.displayOrder),
      isActive: m === 'copy' ? true : r.isActive,
      // Station codes are unique, so a copy starts without stations.
      stations: m === 'copy' ? [] : (r.stations ?? []).map((s) => ({ ...s, key: s.id ?? newKey() })),
      deletedStations: [],
      // An existing kitchen saved without stations stays that way unless asked.
      asStation: m === 'copy',
    };
  }, [defaultPropertyId]);

  const validate = useCallback((f: KitchenForm): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!CODE_PATTERN.test(f.kitchenCode.trim().toUpperCase())) errors.kitchenCode = '2 to 30 letters, digits or underscores, e.g. MAIN.';
    if (!f.kitchenName.trim()) errors.kitchenName = 'The kitchen needs a name.';
    const order = Number(f.displayOrder);
    if (!Number.isInteger(order) || order < 0 || order > 9999) errors.displayOrder = 'A whole number, 0 to 9999.';
    if (f.stations.some((s) => !s.stationCode.trim() || !s.stationName.trim())) errors.stations = 'Every station needs a code and a name.';
    else {
      const codes = f.stations.map((s) => s.stationCode.trim().toUpperCase());
      const dup = codes.find((c, i) => codes.indexOf(c) !== i);
      if (dup) errors.stations = `The code ${dup} is used twice.`;
      else if (f.stations.some((s) => s.printCopies < 1 || s.printCopies > 5)) errors.stations = 'Copies is 1 to 5.';
    }
    return errors;
  }, []);

  const onSave = useCallback(async (f: KitchenForm, m: FormModalMode, r: DineKitchen | null) => {
    const body = {
      kitchenCode: f.kitchenCode.trim().toUpperCase(),
      kitchenName: f.kitchenName.trim(),
      propertyId: f.propertyId || null,
      businessUnitId: f.businessUnitId || null,
      displayOrder: Number(f.displayOrder),
      isActive: f.isActive,
      rowVersion: m === 'edit' && r ? r.rowVersion : 0,
      stations: f.stations.length === 0 && f.asStation
        ? [kitchenAsStation(f)]
        : f.stations.map(({ key, ...s }) => ({
          ...s, stationCode: s.stationCode.trim(), stationName: s.stationName.trim(), printerName: s.printerName?.trim() || null,
        })),
      deleteStationIds: f.deletedStations.map((d) => d.id).filter((id): id is string => !!id),
    };
    if (m === 'edit' && r) await dineOpsService.updateKitchen(r.id, body);
    else await dineOpsService.createKitchen(body);
  }, []);

  const propertyOptions: SelectOption[] = useMemo(
    () => [{ value: '', label: 'Shared by every property' }, ...properties.map((p) => ({ value: p.id, label: p.name }))], [properties]);

  return (
    <EnterpriseFormModal<KitchenForm, DineKitchen>
      isOpen={isOpen}
      mode={mode}
      record={record}
      noun="Kitchen"
      emptyForm={EMPTY_FORM}
      populateOnOpen={populateOnOpen}
      validate={validate}
      onSave={onSave}
      confirmSaveMessage={(m, f) => {
        const count = f.stations.length === 0 && f.asStation ? 1 : f.stations.length;
        const deletes = f.deletedStations.length > 0 ? ` ${f.deletedStations.length} station(s) will be deleted.` : '';
        return m === 'edit'
          ? `Save ${f.kitchenName.trim() || 'this kitchen'} and its ${count} station(s)?${deletes}`
          : `Add the kitchen ${f.kitchenName.trim()} with ${count} station(s)?`;
      }}
      size="lg"
      tabId={tabId}
      moduleName={moduleName}
      createInstanceId={createInstanceId}
      recordKey={record?.id}
      getStripFormLabel={(f) => f.kitchenName || f.kitchenCode || undefined}
      getStripRecordLabel={(r) => (r ? r.kitchenName : undefined)}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {({ form, setForm, mode: m, record: r, getFieldError, clearFieldError, isSaving }) => {
        const set = <K extends keyof KitchenForm>(key: K, value: KitchenForm[K]) => {
          clearFieldError(key as string);
          setForm((prev) => ({ ...prev, [key]: value }));
        };
        const property = properties.find((p) => p.id === form.propertyId);
        return (
          <>
            <div className="form-grid-2">
              <div className="form-group" data-field="kitchenCode">
                <label className="form-label">Code <span className="text-danger">*</span></label>
                <input className={`form-input${getFieldError('kitchenCode') ? ' form-field-error' : ''}`} value={form.kitchenCode}
                  maxLength={30} placeholder="e.g., MAIN" disabled={isSaving} style={{ fontFamily: 'monospace' }}
                  onChange={(e) => set('kitchenCode', e.target.value.toUpperCase().replace(/\s/g, '_'))} />
                {getFieldError('kitchenCode') && <div className="form-error-message">{getFieldError('kitchenCode')}</div>}
              </div>
              <div className="form-group" data-field="kitchenName">
                <label className="form-label">Kitchen name <span className="text-danger">*</span></label>
                <input className={`form-input${getFieldError('kitchenName') ? ' form-field-error' : ''}`} value={form.kitchenName}
                  maxLength={100} placeholder="e.g., Main Kitchen" disabled={isSaving} onChange={(e) => set('kitchenName', e.target.value)} />
                {getFieldError('kitchenName') && <div className="form-error-message">{getFieldError('kitchenName')}</div>}
              </div>
              <div className="form-group" data-field="propertyId">
                <SyutSelect label="Property" options={propertyOptions} value={form.propertyId} isClearable={false} isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  onChange={(v) => setForm((prev) => ({ ...prev, propertyId: (v as string) || '', businessUnitId: '' }))}
                  helperText="Where the kitchen is. Shared = it serves every property." />
              </div>
              <div className="form-group" data-field="businessUnitId">
                <BusinessUnitSelect
                  key={form.propertyId || 'shared'}
                  value={form.businessUnitId}
                  onChange={(v) => set('businessUnitId', v)}
                  entityId={property?.entityId}
                  propertyId={form.propertyId || undefined}
                  types={KITCHEN_UNIT_TYPES}
                  seed={m === 'edit' ? { id: r?.businessUnitId, name: r?.businessUnitName } : undefined}
                  isDisabled={isSaving}
                  placeholder="Not linked yet"
                  helperText="The cost centre its stations' work belongs to, e.g. the F&B department."
                />
              </div>
              <div className="form-group" data-field="displayOrder">
                <label className="form-label">Display order</label>
                <input type="number" className={`form-input${getFieldError('displayOrder') ? ' form-field-error' : ''}`} value={form.displayOrder}
                  min={0} step={10} disabled={isSaving} onChange={(e) => set('displayOrder', e.target.value)} />
                {getFieldError('displayOrder')
                  ? <div className="form-error-message">{getFieldError('displayOrder')}</div>
                  : <FieldHint>Lower comes first in lists.</FieldHint>}
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.isActive} disabled={isSaving} onChange={(e) => set('isActive', e.target.checked)}
                    style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }} />
                  <span style={{ fontSize: '0.85rem' }}>Active</span>
                </label>
              </div>
            </div>
            <StationsEditor
              rows={form.stations}
              onChange={(rows) => { clearFieldError('stations'); setForm((prev) => ({ ...prev, stations: rows })); }}
              deleted={form.deletedStations}
              onDeletedChange={(rows) => setForm((prev) => ({ ...prev, deletedStations: rows }))}
              canDelete={canDeleteStations && m === 'edit'}
              kitchenLabel={[form.kitchenCode.trim().toUpperCase(), form.kitchenName.trim()].filter(Boolean).join(' — ')}
              asStation={form.asStation}
              asStationPrinterId={form.asStationPrinterId}
              asStationFallbackId={form.asStationFallbackId}
              onAsStationChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              kitchenId={m === 'edit' ? r?.id : undefined}
              disabled={isSaving}
              error={getFieldError('stations')}
            />
          </>
        );
      }}
    </EnterpriseFormModal>
  );
};

export default DineOpsKitchenFormModal;
